<template>
  <div
    v-if="discussion"
    class="pb-50 md:pb-100 bg-canvas text-app-color mt-20 md:mt-0 relative min-h-full"
    :class="[
      {'max-w-1100 mx-auto w-full rounded-t-9 bg-canvas' : isPaperShape},
      {'shadow-paperMode': isPaperShape && !isDarkMode}
    ]"
  >
    <div
      class="pt-15 px-20 md:px-25 pb-10 mx-auto min-h-full rounded-9 flex-grow flex flex-col bg-canvas max-w-950"
    >
      <div class="flex">
        <ButtonModule
          text="back"
          prefix="icon-arrow-top text-22 -rotate-90 transform"
          padding="p-0"
          class="absolute top-15 left-15 text-grey-250 hover:text-app-color anim-color"
          :icon-has-margin="false"
          @click="goToDiscussions"
        />
      </div>
      <div class="md:flex justify-between md:ml-150 mb-10 pt-35 md:pt-55">
        <h1 class="text-26 md:text-32 leading-tight pr-20">{{ discussion.title }}</h1>

        <div class="flex items-center justify-end absolute md:static top-15 right-15">
          <div v-if="countMessages > 0" class="flex items-center text-grey-250 mr-10">
            <i class="icon-messages mr-3" />
            <p class="text-14 leading-none">{{ countMessages }}</p>
          </div>
          <div v-if="usersWhoReplied > 0" class="text-grey-250 flex items-center">
            <i class="icon-members text-14 mr-2" />
            <span class="text-14">{{ usersWhoReplied }}</span>
          </div>
          <DropdownModule
            v-if="isOwner"
            icon="icon-more-horizontal text-20"
            :width="150"
            top-position="30px"
            append-to-body
            class="anim-opacity ml-10"
            :class="{'opacity-100': showMenu}"
            remove-optional-top-gap
            :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
            is-auto-menu-position
            @click="showMenu = true"
            @close="showMenu = false"
          >
            <div>
              <div class="hover:bg-selection rounded-t-10 group-scope text-15">
                <ButtonModule
                  text="Edit"
                  :prefix="`icon-edit mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                  class="p-10 w-full"
                  @click="toggleEdit"
                />
              </div>
              <div class="hover:bg-selection group-scope text-15" :class="{'rounded-b-10': !discussion.resolvedAt}">
                <ButtonModule
                  text="Delete"
                  :prefix="`icon-delete mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                  class="p-10 w-full"
                  @click="deleteDiscussion"
                />
              </div>
              <div v-if="discussion.resolvedAt" class="hover:bg-selection group-scope text-15 rounded-b-10">
                <ButtonModule
                  text="Unresolve"
                  :prefix="`icon-unresolve mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                  class="p-10 w-full"
                  @click="unresolve"
                />
              </div>
            </div>
          </DropdownModule>
          <DiscussionModal
            v-if="modalIsOpen"
            :selected-book="selectedBook"
            :discussion="discussion"
            @close="closeModal"
          />
        </div>
      </div>

      <div class="flex flex-col grid-container">
        <div class="flex md:flex-col md:pr-25 h-full mb-10 md:mb-0">
          <UserBadge
            :first-name="discussion.user.profile.firstName"
            :last-name="discussion.user.profile.lastName"
            :color="discussion.user.profile.color"
            color-height="h-18"
            max-width="max-w-95"
            class="text-15 flex items-center text-app-color px-10 py-3 rounded-6 md:ml-auto max-w-125"
            :class="isDarkMode ? 'bg-book' : 'bg-white shadow-user-badge border border-grey-500'"
          />

          <div class="text-12 text-grey-300 md:ml-auto mt-5 flex-shrink-0 ml-10" :title="titleTime">{{ getTime }}</div>
          <div class="w-1/2 border-r h-calc mt-15" :class="isDarkMode ? 'border-sidebar' : 'border-grey-425'" />
        </div>
        <!-- eslint-disable  vue/no-v-html -->
        <div class="group md:pb-20 md:border-b" :class="isDarkMode ? 'border-sidebar' : 'border-grey-425'">
          <DiscussionEditor
            :editable="false"
            class=" order-0 md:order-1"
            :discussion="discussion"
            :book-id="discussion.bookId"
          />
          <Reactions :discussion-id="discussion.id" :message-reactions="discussion.reactions" :book-id="discussion.bookId" class="mt-10" />
        </div>

        <div class="h-50 border-r ml-50 self-start md:hidden mb-10 mt-20" :class="isDarkMode ? 'border-sidebar' : 'border-grey-425'" />
      </div>

      <DiscussionChat :discussion="discussion" :allow-hot-key="!modalIsOpen" />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted, nextTick, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import designConfig from '@/core/design-config'
import { parseDate } from '@/plugins/filters'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DiscussionModal from '@/components/discussions/DiscussionModal'
import DiscussionEditor from '@/components/discussions/DiscussionEditor'
import DiscussionChat from '@/components/discussions/DiscussionChat'
import Reactions from '@/components/discussions/Reactions'

export default defineComponent({
  components: {
    DropdownModule,
    ButtonModule,
    DiscussionModal,
    DiscussionEditor,
    DiscussionChat,
    Reactions
  },

  setup () {
    const { state, commit, getters, dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()

    const showMenu = ref(false)
    const modalIsOpen = ref(false)

    const discussion = computed(() => state.discussions.discussion)
    const maxWidth = computed(() => `max-w-${designConfig.columns[settings.value?.columnWidth]}`)

    const settings = computed(() => state.settings.settings)

    const usersWhoReplied = computed(() => {
      const users = state.discussions.messages.map(item => item.userId)
      const uniqueUsers = [...new Set(users)]
      return uniqueUsers.length
    })

    const isOwner = computed(() => discussion.value.user.id === state.profile.user.id)
    const countMessages = computed(() => state.discussions.messages.length)

    const getTime = computed(() => {
      const minutes = (Date.now() - new Date(discussion.value.createdAt)) / 1000 / 60
      if (minutes < 1) {
        return 'now'
      } else if (minutes < 2) {
        return '1 minute ago'
      } else if (minutes < 60) {
        return `${parseInt(minutes)} minutes ago`
      } else if (minutes / 60 < 2) {
        return '1 hour ago'
      } else if (minutes / 60 < 24) {
        return `${parseInt(minutes / 60)} hours ago`
      } else if (minutes / 60 > 24 && minutes / 60 / 24 < 8) {
        if (parseInt(minutes / 60 / 24) === 1) return `${parseInt(minutes / 60 / 24)} day ago`
        return `${parseInt(minutes / 60 / 24)} days ago`
      }
      return parseDate(discussion.value.createdAt, 'DD-MM-YYYY')
    })

    const titleTime = computed(() => {
      return parseDate(discussion.value.createdAt, 'DD-MM-YYYY hh:mm')
    })

    const headquarters = computed(() => state.team.headquarters)

    const allBooks = computed(() => {
      const result = state.book.books.filter(b => !b.isSection)
      result.push(headquarters.value)
      return result
    })
    const selectedBook = computed(() => allBooks.value.find(b => b.id === parseInt(discussion.value.bookId)))

    const isDarkMode = computed(() => getters.isDarkMode)
    const isPaperShape = computed(() => getters.isPaperShape)
    const isPoseidonTheme = computed(() => getters.isPoseidonTheme)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)

    watch(() => route.params.id, (to) => {
      if (to) {
        router.push({
          name: 'Discussions',
          params: { id: to },
          query: { spaces: to }
        })
      }
    })

    onMounted(() => {
      nextTick(async () => {
        await dispatch('getDiscussion', { id: route.params.discussionId, bookId: route.params.id })
          .then(res => {
            document.title = res.title + ' - hello ivy'
          })

        await dispatch('getDiscussionMessages', {
          discussionId: route.params.discussionId,
          bookId: route.params.id,
          page: 0
        })

        dispatch('getBook', route.params.id).then(res => {
          if (res.id && res.chat?.id) {
            dispatch('getMessages', {
              bookId: res.id,
              chatId: res.chat.id
            })
          }
        })
        dispatch('getReactions')
      })
    })

    onBeforeUnmount(() => {
      commit('setDiscussion', null)
    })

    function toggleEdit () {
      modalIsOpen.value = true
    }

    function closeModal () {
      modalIsOpen.value = false
    }

    async function deleteDiscussion () {
      await dispatch('deleteDiscussion', {
        id: discussion.value.id,
        bookId: discussion.value.bookId
      })

      router.push({ name: 'Discussions', params: { id: discussion.value.bookId }, query: { spaces: discussion.value.bookId } })
    }

    function goToDiscussions () {
      router.push({
        name: 'Discussions',
        params: { id: route.params.id },
        query: { spaces: route.params.id }
      })
    }

    function unresolve () {
      const message = state.discussions.messages.find(item => item.resolvedAt)
      dispatch('resolve', {
        messageId: message.id,
        discussionId: discussion.value.id,
        bookId: discussion.value.bookId,
        resolvedAt: null
      })
    }

    return {
      // data
      showMenu,
      modalIsOpen,
      // computed
      isOwner,
      getTime,
      maxWidth,
      settings,
      titleTime,
      isDarkMode,
      discussion,
      isPaperShape,
      selectedBook,
      countMessages,
      isTwilightTheme,
      isPoseidonTheme,
      usersWhoReplied,
      // functions
      unresolve,
      toggleEdit,
      closeModal,
      goToDiscussions,
      deleteDiscussion
    }
  }
})
</script>

<style>
@media (min-width: 768px) {
  .grid-container {
    display: grid;
    grid-template-columns: 150px 1fr;
  }
}

.h-calc {
  height: calc(100% - 70px);
}
</style>
